import { b as isLocalEnvironment, l as logger, g as getConfigEnvPrefix, f as fetchLocally, s as saveLocally, e as environment, O as OcapiLinkType, S as Subtype, c as clone, B as BusinessContext } from './logger-faaf508a.js';
import { u as updateLocalStore, g as getLocalStore } from './index-4d2624c8.js';
import { S as SERVICE, a as getContainerItems, i as isResponseValid, b as addDisclaimerIndexToItems, c as getContainersMap, d as getDisclaimerParams, F as FULL_SUMMARY, e as getContainerLabel, O as OUTPUT, m as modifyInputs, f as getParamValue, h as getLabelText, j as SHORT_SUMMARY, R as REGULATORY } from './utils-04aedd60.js';

var global =
  (typeof globalThis !== 'undefined' && globalThis) ||
  (typeof self !== 'undefined' && self) ||
  (typeof global !== 'undefined' && global);

var support = {
  searchParams: 'URLSearchParams' in global,
  iterable: 'Symbol' in global && 'iterator' in Symbol,
  blob:
    'FileReader' in global &&
    'Blob' in global &&
    (function() {
      try {
        new Blob();
        return true
      } catch (e) {
        return false
      }
    })(),
  formData: 'FormData' in global,
  arrayBuffer: 'ArrayBuffer' in global
};

function isDataView(obj) {
  return obj && DataView.prototype.isPrototypeOf(obj)
}

if (support.arrayBuffer) {
  var viewClasses = [
    '[object Int8Array]',
    '[object Uint8Array]',
    '[object Uint8ClampedArray]',
    '[object Int16Array]',
    '[object Uint16Array]',
    '[object Int32Array]',
    '[object Uint32Array]',
    '[object Float32Array]',
    '[object Float64Array]'
  ];

  var isArrayBufferView =
    ArrayBuffer.isView ||
    function(obj) {
      return obj && viewClasses.indexOf(Object.prototype.toString.call(obj)) > -1
    };
}

function normalizeName(name) {
  if (typeof name !== 'string') {
    name = String(name);
  }
  if (/[^a-z0-9\-#$%&'*+.^_`|~!]/i.test(name) || name === '') {
    throw new TypeError('Invalid character in header field name: "' + name + '"')
  }
  return name.toLowerCase()
}

function normalizeValue(value) {
  if (typeof value !== 'string') {
    value = String(value);
  }
  return value
}

// Build a destructive iterator for the value list
function iteratorFor(items) {
  var iterator = {
    next: function() {
      var value = items.shift();
      return {done: value === undefined, value: value}
    }
  };

  if (support.iterable) {
    iterator[Symbol.iterator] = function() {
      return iterator
    };
  }

  return iterator
}

function Headers(headers) {
  this.map = {};

  if (headers instanceof Headers) {
    headers.forEach(function(value, name) {
      this.append(name, value);
    }, this);
  } else if (Array.isArray(headers)) {
    headers.forEach(function(header) {
      this.append(header[0], header[1]);
    }, this);
  } else if (headers) {
    Object.getOwnPropertyNames(headers).forEach(function(name) {
      this.append(name, headers[name]);
    }, this);
  }
}

Headers.prototype.append = function(name, value) {
  name = normalizeName(name);
  value = normalizeValue(value);
  var oldValue = this.map[name];
  this.map[name] = oldValue ? oldValue + ', ' + value : value;
};

Headers.prototype['delete'] = function(name) {
  delete this.map[normalizeName(name)];
};

Headers.prototype.get = function(name) {
  name = normalizeName(name);
  return this.has(name) ? this.map[name] : null
};

Headers.prototype.has = function(name) {
  return this.map.hasOwnProperty(normalizeName(name))
};

Headers.prototype.set = function(name, value) {
  this.map[normalizeName(name)] = normalizeValue(value);
};

Headers.prototype.forEach = function(callback, thisArg) {
  for (var name in this.map) {
    if (this.map.hasOwnProperty(name)) {
      callback.call(thisArg, this.map[name], name, this);
    }
  }
};

Headers.prototype.keys = function() {
  var items = [];
  this.forEach(function(value, name) {
    items.push(name);
  });
  return iteratorFor(items)
};

Headers.prototype.values = function() {
  var items = [];
  this.forEach(function(value) {
    items.push(value);
  });
  return iteratorFor(items)
};

Headers.prototype.entries = function() {
  var items = [];
  this.forEach(function(value, name) {
    items.push([name, value]);
  });
  return iteratorFor(items)
};

if (support.iterable) {
  Headers.prototype[Symbol.iterator] = Headers.prototype.entries;
}

function consumed(body) {
  if (body.bodyUsed) {
    return Promise.reject(new TypeError('Already read'))
  }
  body.bodyUsed = true;
}

function fileReaderReady(reader) {
  return new Promise(function(resolve, reject) {
    reader.onload = function() {
      resolve(reader.result);
    };
    reader.onerror = function() {
      reject(reader.error);
    };
  })
}

function readBlobAsArrayBuffer(blob) {
  var reader = new FileReader();
  var promise = fileReaderReady(reader);
  reader.readAsArrayBuffer(blob);
  return promise
}

function readBlobAsText(blob) {
  var reader = new FileReader();
  var promise = fileReaderReady(reader);
  reader.readAsText(blob);
  return promise
}

function readArrayBufferAsText(buf) {
  var view = new Uint8Array(buf);
  var chars = new Array(view.length);

  for (var i = 0; i < view.length; i++) {
    chars[i] = String.fromCharCode(view[i]);
  }
  return chars.join('')
}

function bufferClone(buf) {
  if (buf.slice) {
    return buf.slice(0)
  } else {
    var view = new Uint8Array(buf.byteLength);
    view.set(new Uint8Array(buf));
    return view.buffer
  }
}

function Body() {
  this.bodyUsed = false;

  this._initBody = function(body) {
    /*
      fetch-mock wraps the Response object in an ES6 Proxy to
      provide useful test harness features such as flush. However, on
      ES5 browsers without fetch or Proxy support pollyfills must be used;
      the proxy-pollyfill is unable to proxy an attribute unless it exists
      on the object before the Proxy is created. This change ensures
      Response.bodyUsed exists on the instance, while maintaining the
      semantic of setting Request.bodyUsed in the constructor before
      _initBody is called.
    */
    this.bodyUsed = this.bodyUsed;
    this._bodyInit = body;
    if (!body) {
      this._bodyText = '';
    } else if (typeof body === 'string') {
      this._bodyText = body;
    } else if (support.blob && Blob.prototype.isPrototypeOf(body)) {
      this._bodyBlob = body;
    } else if (support.formData && FormData.prototype.isPrototypeOf(body)) {
      this._bodyFormData = body;
    } else if (support.searchParams && URLSearchParams.prototype.isPrototypeOf(body)) {
      this._bodyText = body.toString();
    } else if (support.arrayBuffer && support.blob && isDataView(body)) {
      this._bodyArrayBuffer = bufferClone(body.buffer);
      // IE 10-11 can't handle a DataView body.
      this._bodyInit = new Blob([this._bodyArrayBuffer]);
    } else if (support.arrayBuffer && (ArrayBuffer.prototype.isPrototypeOf(body) || isArrayBufferView(body))) {
      this._bodyArrayBuffer = bufferClone(body);
    } else {
      this._bodyText = body = Object.prototype.toString.call(body);
    }

    if (!this.headers.get('content-type')) {
      if (typeof body === 'string') {
        this.headers.set('content-type', 'text/plain;charset=UTF-8');
      } else if (this._bodyBlob && this._bodyBlob.type) {
        this.headers.set('content-type', this._bodyBlob.type);
      } else if (support.searchParams && URLSearchParams.prototype.isPrototypeOf(body)) {
        this.headers.set('content-type', 'application/x-www-form-urlencoded;charset=UTF-8');
      }
    }
  };

  if (support.blob) {
    this.blob = function() {
      var rejected = consumed(this);
      if (rejected) {
        return rejected
      }

      if (this._bodyBlob) {
        return Promise.resolve(this._bodyBlob)
      } else if (this._bodyArrayBuffer) {
        return Promise.resolve(new Blob([this._bodyArrayBuffer]))
      } else if (this._bodyFormData) {
        throw new Error('could not read FormData body as blob')
      } else {
        return Promise.resolve(new Blob([this._bodyText]))
      }
    };

    this.arrayBuffer = function() {
      if (this._bodyArrayBuffer) {
        var isConsumed = consumed(this);
        if (isConsumed) {
          return isConsumed
        }
        if (ArrayBuffer.isView(this._bodyArrayBuffer)) {
          return Promise.resolve(
            this._bodyArrayBuffer.buffer.slice(
              this._bodyArrayBuffer.byteOffset,
              this._bodyArrayBuffer.byteOffset + this._bodyArrayBuffer.byteLength
            )
          )
        } else {
          return Promise.resolve(this._bodyArrayBuffer)
        }
      } else {
        return this.blob().then(readBlobAsArrayBuffer)
      }
    };
  }

  this.text = function() {
    var rejected = consumed(this);
    if (rejected) {
      return rejected
    }

    if (this._bodyBlob) {
      return readBlobAsText(this._bodyBlob)
    } else if (this._bodyArrayBuffer) {
      return Promise.resolve(readArrayBufferAsText(this._bodyArrayBuffer))
    } else if (this._bodyFormData) {
      throw new Error('could not read FormData body as text')
    } else {
      return Promise.resolve(this._bodyText)
    }
  };

  if (support.formData) {
    this.formData = function() {
      return this.text().then(decode)
    };
  }

  this.json = function() {
    return this.text().then(JSON.parse)
  };

  return this
}

// HTTP methods whose capitalization should be normalized
var methods = ['DELETE', 'GET', 'HEAD', 'OPTIONS', 'POST', 'PUT'];

function normalizeMethod(method) {
  var upcased = method.toUpperCase();
  return methods.indexOf(upcased) > -1 ? upcased : method
}

function Request(input, options) {
  if (!(this instanceof Request)) {
    throw new TypeError('Please use the "new" operator, this DOM object constructor cannot be called as a function.')
  }

  options = options || {};
  var body = options.body;

  if (input instanceof Request) {
    if (input.bodyUsed) {
      throw new TypeError('Already read')
    }
    this.url = input.url;
    this.credentials = input.credentials;
    if (!options.headers) {
      this.headers = new Headers(input.headers);
    }
    this.method = input.method;
    this.mode = input.mode;
    this.signal = input.signal;
    if (!body && input._bodyInit != null) {
      body = input._bodyInit;
      input.bodyUsed = true;
    }
  } else {
    this.url = String(input);
  }

  this.credentials = options.credentials || this.credentials || 'same-origin';
  if (options.headers || !this.headers) {
    this.headers = new Headers(options.headers);
  }
  this.method = normalizeMethod(options.method || this.method || 'GET');
  this.mode = options.mode || this.mode || null;
  this.signal = options.signal || this.signal;
  this.referrer = null;

  if ((this.method === 'GET' || this.method === 'HEAD') && body) {
    throw new TypeError('Body not allowed for GET or HEAD requests')
  }
  this._initBody(body);

  if (this.method === 'GET' || this.method === 'HEAD') {
    if (options.cache === 'no-store' || options.cache === 'no-cache') {
      // Search for a '_' parameter in the query string
      var reParamSearch = /([?&])_=[^&]*/;
      if (reParamSearch.test(this.url)) {
        // If it already exists then set the value with the current time
        this.url = this.url.replace(reParamSearch, '$1_=' + new Date().getTime());
      } else {
        // Otherwise add a new '_' parameter to the end with the current time
        var reQueryString = /\?/;
        this.url += (reQueryString.test(this.url) ? '&' : '?') + '_=' + new Date().getTime();
      }
    }
  }
}

Request.prototype.clone = function() {
  return new Request(this, {body: this._bodyInit})
};

function decode(body) {
  var form = new FormData();
  body
    .trim()
    .split('&')
    .forEach(function(bytes) {
      if (bytes) {
        var split = bytes.split('=');
        var name = split.shift().replace(/\+/g, ' ');
        var value = split.join('=').replace(/\+/g, ' ');
        form.append(decodeURIComponent(name), decodeURIComponent(value));
      }
    });
  return form
}

function parseHeaders(rawHeaders) {
  var headers = new Headers();
  // Replace instances of \r\n and \n followed by at least one space or horizontal tab with a space
  // https://tools.ietf.org/html/rfc7230#section-3.2
  var preProcessedHeaders = rawHeaders.replace(/\r?\n[\t ]+/g, ' ');
  // Avoiding split via regex to work around a common IE11 bug with the core-js 3.6.0 regex polyfill
  // https://github.com/github/fetch/issues/748
  // https://github.com/zloirock/core-js/issues/751
  preProcessedHeaders
    .split('\r')
    .map(function(header) {
      return header.indexOf('\n') === 0 ? header.substr(1, header.length) : header
    })
    .forEach(function(line) {
      var parts = line.split(':');
      var key = parts.shift().trim();
      if (key) {
        var value = parts.join(':').trim();
        headers.append(key, value);
      }
    });
  return headers
}

Body.call(Request.prototype);

function Response$1(bodyInit, options) {
  if (!(this instanceof Response$1)) {
    throw new TypeError('Please use the "new" operator, this DOM object constructor cannot be called as a function.')
  }
  if (!options) {
    options = {};
  }

  this.type = 'default';
  this.status = options.status === undefined ? 200 : options.status;
  this.ok = this.status >= 200 && this.status < 300;
  this.statusText = options.statusText === undefined ? '' : '' + options.statusText;
  this.headers = new Headers(options.headers);
  this.url = options.url || '';
  this._initBody(bodyInit);
}

Body.call(Response$1.prototype);

Response$1.prototype.clone = function() {
  return new Response$1(this._bodyInit, {
    status: this.status,
    statusText: this.statusText,
    headers: new Headers(this.headers),
    url: this.url
  })
};

Response$1.error = function() {
  var response = new Response$1(null, {status: 0, statusText: ''});
  response.type = 'error';
  return response
};

var redirectStatuses = [301, 302, 303, 307, 308];

Response$1.redirect = function(url, status) {
  if (redirectStatuses.indexOf(status) === -1) {
    throw new RangeError('Invalid status code')
  }

  return new Response$1(null, {status: status, headers: {location: url}})
};

var DOMException = global.DOMException;
try {
  new DOMException();
} catch (err) {
  DOMException = function(message, name) {
    this.message = message;
    this.name = name;
    var error = Error(message);
    this.stack = error.stack;
  };
  DOMException.prototype = Object.create(Error.prototype);
  DOMException.prototype.constructor = DOMException;
}

function fetch(input, init) {
  return new Promise(function(resolve, reject) {
    var request = new Request(input, init);

    if (request.signal && request.signal.aborted) {
      return reject(new DOMException('Aborted', 'AbortError'))
    }

    var xhr = new XMLHttpRequest();

    function abortXhr() {
      xhr.abort();
    }

    xhr.onload = function() {
      var options = {
        status: xhr.status,
        statusText: xhr.statusText,
        headers: parseHeaders(xhr.getAllResponseHeaders() || '')
      };
      options.url = 'responseURL' in xhr ? xhr.responseURL : options.headers.get('X-Request-URL');
      var body = 'response' in xhr ? xhr.response : xhr.responseText;
      setTimeout(function() {
        resolve(new Response$1(body, options));
      }, 0);
    };

    xhr.onerror = function() {
      setTimeout(function() {
        reject(new TypeError('Network request failed'));
      }, 0);
    };

    xhr.ontimeout = function() {
      setTimeout(function() {
        reject(new TypeError('Network request failed'));
      }, 0);
    };

    xhr.onabort = function() {
      setTimeout(function() {
        reject(new DOMException('Aborted', 'AbortError'));
      }, 0);
    };

    function fixUrl(url) {
      try {
        return url === '' && global.location.href ? global.location.href : url
      } catch (e) {
        return url
      }
    }

    xhr.open(request.method, fixUrl(request.url), true);

    if (request.credentials === 'include') {
      xhr.withCredentials = true;
    } else if (request.credentials === 'omit') {
      xhr.withCredentials = false;
    }

    if ('responseType' in xhr) {
      if (support.blob) {
        xhr.responseType = 'blob';
      } else if (
        support.arrayBuffer &&
        request.headers.get('Content-Type') &&
        request.headers.get('Content-Type').indexOf('application/octet-stream') !== -1
      ) {
        xhr.responseType = 'arraybuffer';
      }
    }

    if (init && typeof init.headers === 'object' && !(init.headers instanceof Headers)) {
      Object.getOwnPropertyNames(init.headers).forEach(function(name) {
        xhr.setRequestHeader(name, normalizeValue(init.headers[name]));
      });
    } else {
      request.headers.forEach(function(value, name) {
        xhr.setRequestHeader(name, value);
      });
    }

    if (request.signal) {
      request.signal.addEventListener('abort', abortXhr);

      xhr.onreadystatechange = function() {
        // DONE (success or failure)
        if (xhr.readyState === 4) {
          request.signal.removeEventListener('abort', abortXhr);
        }
      };
    }

    xhr.send(typeof request._bodyInit === 'undefined' ? null : request._bodyInit);
  })
}

fetch.polyfill = true;

if (!global.fetch) {
  global.fetch = fetch;
  global.Headers = Headers;
  global.Request = Request;
  global.Response = Response$1;
}

// the whatwg-fetch polyfill installs the fetch() function
// on the global object (window or self)
//
// Return that as the export for use in Webpack, Browserify etc.

var fetchNpmBrowserify = self.fetch.bind(self);

function http(input, init) {
  return fetchNpmBrowserify(input, init)
    .then((response) => {
    if (!response.ok) {
      throw response;
    }
    return response;
  })
    .then((response) => {
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.indexOf('application/json') !== -1) {
      return response.json();
    }
    return null;
  });
}

const CALLERS_KEY = 'fcis:callers';
const CONFIG_KEY = 'fcis:config';
const TRANSLATIONS_KEY = 'fcis:translations';
const defaultConfigRequest = async (url, errorMessage, objectKey, retainObject) => {
  let configuration = fetchLocally(objectKey);
  if (configuration) {
    return configuration;
  }
  try {
    const response = await http(url);
    if (retainObject) {
      saveLocally(objectKey, response);
    }
    return response;
  }
  catch (err) {
    const error = {
      code: 'CONFIG_ERROR',
      message: 'Config failure',
      statusCode: err.statusCode,
    };
    if (error.statusCode === 404) {
      error.message = errorMessage;
    }
    logger.log(error)();
    return Promise.reject(error);
  }
};
class ConfigService {
  constructor() {
    this.retainConfig = !isLocalEnvironment();
  }
  async fetchAvailableConfigs() {
    const url = `https://${this.configurationEnvironment}assets.oneweb.mercedes-benz.com/plugin/fcis/fcis-config/v2/callers.json`;
    return await defaultConfigRequest(url, 'Failed to fetch callers file', `${CALLERS_KEY}:${environment.environment}`, this.retainConfig);
  }
  async fetchDefaultConfig(env) {
    const objectKey = `${CONFIG_KEY}:default:${environment.environment}`;
    const url = `https://${env}assets.oneweb.mercedes-benz.com/plugin/fcis/fcis-config/v2/default/DEFAULT/default_DEFAULT.json`;
    // const url = `fixtures/configs/default/DEFAULT/default_DEFAULT.json`;
    return await defaultConfigRequest(url, 'Failed to fetch default configuration', objectKey, this.retainConfig);
  }
  paramsHasCustomConfiguration(availableConfigs, config) {
    const caller = availableConfigs.find((item) => item.callerName === this.configurationCaller);
    if (caller) {
      const market = caller.markets.find((item) => item.code === config.market);
      if (market) {
        return market.locales.indexOf(config.locale) > -1;
      }
    }
    return false;
  }
  getApiKeyForCaller(availableConfigs) {
    const caller = availableConfigs.find((item) => item.callerName === this.configurationCaller);
    return caller ? caller.apiKey : '';
  }
  async fetchCustomConfiguration(storeId, config) {
    const localKey = `${CONFIG_KEY}:${config.caller}:${config.locale}:${environment.environment}`;
    // FIXTURE
    // leave commented for ease of switching to local testing
    // const url = `fixtures/configs/${configCaller}/${config.market}/${config.locale}.json`
    const url = `https://${this.configurationEnvironment}assets.oneweb.mercedes-benz.com/plugin/fcis/fcis-config/v2/${this.configurationCaller}/${config.market}/${config.locale}.json`;
    logger.log('Getting the config from ', url)();
    const errorMessage = `Configuration for '${config.caller}' in '${config.market}' market with '${config.locale}'
          locale does not exist. Please check your initialization parameters`;
    const data = await defaultConfigRequest(url, errorMessage, localKey, this.retainConfig);
    this.config = Object.assign({}, data, config.customConfig);
    updateLocalStore(storeId, { config: this.config });
  }
  async setDefaultConfiguration(storeId, config) {
    const response = await this.fetchDefaultConfig(this.configurationEnvironment);
    const stringToReplace = '{default}';
    const vdsMarket = response.vehicleDataSource.euMarkets.includes(config.market)
      ? 'eu'
      : config.market.toLowerCase();
    response.ocapiParams.url = response.ocapiParams.url.replace(stringToReplace, config.market.toLowerCase());
    response.vehicleDataSource.url = response.vehicleDataSource.url.replace(stringToReplace, vdsMarket);
    this.config = Object.assign({}, response, config.customConfig);
    updateLocalStore(storeId, { config: this.config });
  }
  async fetchTranslations(config) {
    const localKey = `${TRANSLATIONS_KEY}:${config.locale}:${environment.environment}`;
    const language = config.locale.slice(0, 2);
    const url = `https://${this.configurationEnvironment}assets.oneweb.mercedes-benz.com/plugin/fcis/fcis-config/translations/${config.market}/${language}_${config.market}.json`;
    logger.log('Getting the translations from ', url)();
    const errorMessage = `Translation for '${config.market}' market with '${config.locale}' locale does not exist. Please check your initialization parameters`;
    try {
      return await defaultConfigRequest(url, errorMessage, localKey, this.retainConfig);
    }
    catch (e) {
      logger.error(e);
    }
  }
  async fetchConfig(storeId, configuration) {
    this.configurationEnvironment = getConfigEnvPrefix(environment.environment);
    this.configurationCaller =
      configuration.configCaller || configuration.caller;
    const configs = await this.fetchAvailableConfigs();
    this.apiKey = this.getApiKeyForCaller(configs);
    if (this.paramsHasCustomConfiguration(configs, configuration)) {
      await this.fetchCustomConfiguration(storeId, configuration);
      return getLocalStore(storeId).config;
    }
    else {
      await this.setDefaultConfiguration(storeId, configuration);
      return getLocalStore(storeId).config;
    }
  }
  get ocapiVersion() {
    return this.config.ocapiParams.version;
  }
  ocapiUrl(storeId) {
    return getLocalStore(storeId).config.ocapiParams.url;
  }
  get buttons() {
    return this.config.buttons || [];
  }
  get linksBlacklisted() {
    return this.config.linkBlacklist || [];
  }
}
const configService = new ConfigService();

/**
 * getServices
 * @param containers - Input Container
 * @returns {IInputContainer | null} A childContainer of id service or null
 */
const getServices = (containers = []) => {
  return Array.isArray(containers) && containers.length
    ? containers.find((container) => container.id === SERVICE)
    : null;
};
const filterAPILinks = (linkBlacklist, array = []) => {
  return array.filter((item) => !linkBlacklist.includes(item.id));
};
/**
 *
 * @param links - Response Links
 * @param configButtons - Buttons coming from fc-config
 * @param linkBlacklist - Links / buttons to be removed
 * @returns ILink[]
 */
const modifyLinks = (links, configButtons = [], linkBlacklist = []) => {
  const APILinks = filterAPILinks(linkBlacklist, links);
  const allLinks = [...configButtons, ...APILinks];
  return allLinks.map((item) => {
    if (item.classification !== OcapiLinkType.LIST) {
      return item;
    }
    else {
      const link = item.url;
      item.label = item.label
        .replace('[[', `<a class="wb-link wb-link--inline" href=${link} target="_blank">`)
        .replace(']]', '</a>');
      return item;
    }
  });
};
const getBannerParams = (container) => {
  const regulatoryParams = getContainerItems(container);
  const urlParam = regulatoryParams.find((param) => param.id === 'bannerUrl');
  const textParam = regulatoryParams.find((param) => param.id === 'bannerText');
  if (!urlParam && !textParam) {
    return null;
  }
  return {
    url: urlParam ? urlParam.value : '',
    html: textParam ? textParam.value : '',
  };
};

const displayTextIsRate = (container) => {
  const items = getContainerItems(container);
  return items.length && items[0].subtype
    ? items[0].subtype.toUpperCase() === Subtype.CURRENCY
    : false;
};

class QuoteService {
  constructor(configurationService = configService) {
    this.headers = { 'Content-Type': 'application/json' };
    this.configService = configurationService;
  }
  /**
   * Create a clean quote request without any interference of previous calculations.
   * The method will cleanup:
   * - opaque value
   * - input values
   * - financialCode
   * @param payload
   */
  async getCleanQuote(storeId, payload) {
    if (!payload) {
      payload = getLocalStore(storeId).payloadState;
    }
    const cleanOpaque = '';
    const cleanInput = [];
    const cleanVehicle = Object.assign({}, payload.vehicle, {
      financialCode: null,
    });
    const cleanCustomer = Object.assign({}, payload.customer);
    const cleanValues = {
      vehicle: cleanVehicle,
      opaque: cleanOpaque,
      input: cleanInput,
      customer: cleanCustomer,
    };
    const cleanPayload = Object.assign({}, payload, cleanValues);
    return this.getQuote(storeId, cleanPayload);
  }
  getQuote(storeId, payload) {
    if (!payload) {
      payload = getLocalStore(storeId).payloadState;
    }
    const { locale } = payload.context;
    const ocapiUrl = configService.ocapiUrl(storeId);
    const bodyData = {
      vehicle: payload.vehicle,
      opaque: payload.opaque,
      input: payload.input,
      customer: payload.customer,
    };
    const userInteractionHeaderValue = payload.input.length
      ? 'calculation-changed'
      : 'calculator-viewed';
    this.headers['x-api-key'] = this.configService.apiKey;
    this.headers['X-OCAPI-User-Interaction'] = userInteractionHeaderValue;
    this.headers['accept-language'] = locale.replace('_', '-');
    const requestOptions = {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(bodyData),
    };
    return http(ocapiUrl, requestOptions)
      .then((response) => {
      isResponseValid(response);
      const rawResponse = clone(response);
      const { containers } = response.output;
      addDisclaimerIndexToItems(containers, FULL_SUMMARY, payload.context.locale);
      addDisclaimerIndexToItems(containers, OUTPUT, payload.context.locale);
      this.links = response.output.links || [];
      this.inputChildContainers = response.input.childContainers;
      const { containersMap, childContainersMap } = getContainersMap(containers);
      // Assign output container, and child containers mapped to the
      // respective object attributes
      this.outputContainers = containersMap;
      this.outputChildContainersMap = childContainersMap;
      return this.generateQuote(rawResponse);
    })
      .catch(async (response) => {
      if (!(response instanceof Response)) {
        logger.trace(response)();
        return Promise.reject(response);
      }
      let error = {
        statusCode: response.status,
        code: 'ONE_CALC_ERROR',
      };
      const contentType = response.headers.get('content-type');
      // try to extract error info from broken error response coming from eFF
      if (contentType && contentType.includes('text/html')) {
        const text = await response.text();
        const textRows = text.split(/\r?\n/);
        const lastRow = textRows[textRows.length - 1];
        if (lastRow.startsWith('{')) {
          const backendError = JSON.parse(lastRow);
          error = Object.assign(Object.assign({}, error), { message: backendError.error.message, code: backendError.error.code });
        }
      }
      else if (contentType && contentType.includes('application/json')) {
        const resp = await response.json();
        error.message = resp.error.message;
        error.code = resp.error.code;
      }
      logger.trace(error)();
      return Promise.reject(error);
    });
  }
  /**
   * getChildContainer: Used to retrieve a child container
   * from the object that maps all the child containers inside response.output.containers
   * @param parentContainerName - Parent container: output, fullSummary, ...
   * @param childContainerName - Child container name: service,...
   * @returns - Returns container or undefined
   */
  getChildContainer(parentContainerName, childContainerName) {
    const container = this.outputChildContainersMap[parentContainerName];
    return container ? container[childContainerName] : undefined;
  }
  getFinanceProductInput(inputItems = []) {
    return inputItems.find((item) => item.businessContext === BusinessContext.FUNDING_PRODUCT);
  }
  getCustomerTypeInput(inputItems = []) {
    return inputItems.find((item) => item.businessContext === BusinessContext.CUSTOMER_TYPE);
  }
  getSummaryQuoteData() {
    const containers = this.outputContainers;
    const regulatory = containers[REGULATORY];
    const fullSummary = containers[FULL_SUMMARY];
    const shortSummary = containers[SHORT_SUMMARY];
    const regulatoryParams = getContainerItems(regulatory);
    const fullSummaryParams = getContainerItems(fullSummary);
    const disclaimerParams = getDisclaimerParams(fullSummary);
    const shortSummaryParams = getContainerItems(shortSummary);
    const service = this.getChildContainer(FULL_SUMMARY, SERVICE);
    return {
      title: getContainerLabel(fullSummary),
      displayTextIsRate: displayTextIsRate(shortSummary),
      regulatoryParams,
      fullSummaryParams,
      shortSummaryParams,
      summaryDisclaimerParams: disclaimerParams,
      fullSummaryChildParams: (service === null || service === void 0 ? void 0 : service.items) || [],
    };
  }
  getCleopatraQuoteData() {
    const containers = this.outputContainers;
    const buttons = this.configService.buttons;
    const outputParams = containers[OUTPUT];
    const fullSummary = containers[FULL_SUMMARY];
    const serviceContainer = this.getChildContainer(OUTPUT, SERVICE);
    const childContainerTitle = getContainerLabel(serviceContainer);
    const fullSummaryChildContainerTitle = getContainerLabel(fullSummary);
    const linksBlackListed = this.configService.linksBlacklisted;
    return {
      childContainerTitle,
      fullSummaryChildContainerTitle,
      services: getServices(this.inputChildContainers),
      outputParams: outputParams.items,
      outputTitle: getContainerLabel(containers[OUTPUT]),
      legalBanner: getBannerParams(containers[REGULATORY]),
      links: modifyLinks(this.links, buttons, linksBlackListed),
      outputChildParams: (serviceContainer === null || serviceContainer === void 0 ? void 0 : serviceContainer.items) || [],
    };
  }
  /**
   * generateQuote: Wrapper method that generates the quotation response
   * @param response - OCAPI response
   * @returns {IQuoteResponse}
   */
  generateQuote(response) {
    const { input } = response;
    const _input = Object.assign(Object.assign({}, input), { items: modifyInputs(input.items) });
    const disclaimerParams = getDisclaimerParams(this.outputContainers[OUTPUT]);
    const inputParams = input.items;
    return Object.assign(Object.assign(Object.assign({ quoteResponse: Object.assign(Object.assign({}, response), { input: _input }), rate: getParamValue(this.outputContainers[SHORT_SUMMARY]), label: getLabelText(this.outputContainers[SHORT_SUMMARY]), displayText: getParamValue(this.outputContainers[SHORT_SUMMARY]), rawResponse: response }, this.getSummaryQuoteData()), this.getCleopatraQuoteData()), { disclaimerParams, customerTypeInput: this.getCustomerTypeInput(inputParams) || null, financeProductInput: this.getFinanceProductInput(inputParams) || null });
  }
}
const quoteService = new QuoteService();

export { ConfigService as C, QuoteService as Q, configService as c, http as h, modifyLinks as m, quoteService as q };
